import { useMemo } from 'react';
import type {
  AssetSelectOption,
  InfoMessageConfigKey,
  InternalTransferInfoMessageConfig,
  SectionType,
} from './types';
import type { AccountType } from '@/entities/account';
import type { TransferInternalSettingsDictionary } from '@/shared/types';

const INFO_MESSAGE_CONFIGS: Record<
  InfoMessageConfigKey,
  InternalTransferInfoMessageConfig
> = {
  default_option: {
    show: false,
  },
  ewallet_ewallet: {
    show: true,
    translationKey: 'internalTransferModal.infoMessages.toEwalletEwallet',
    linkAction: 'navigate',
    linkTarget: '/exchange',
  },
  spot_spot: {
    show: true,
    translationKey: 'internalTransferModal.infoMessages.toSpotSpot',
    linkAction: 'external',
    linkTarget: language => `https://my.free2ex.com/${language}/Login`,
  },
  no_convert_assets: {
    show: true,
    translationKey: 'internalTransferModal.noAssets',
    linkAction: 'navigate',
    linkTarget: '/exchange',
  },
};

interface IInternalTransferInfoMessageParams {
  transferInternalSettings: TransferInternalSettingsDictionary | undefined;
  assetSelectOptions: AssetSelectOption[];
  accountWithAssetOptions: AssetSelectOption[];
  isAssetSelector: boolean;
  sectionType: SectionType;
  selectedAccountTypeFrom?: AccountType | null;
  selectedAccountTypeTo?: AccountType | null;
  watchedAssetFromCode: string;
  accountType: AccountType;
}

export const useInternalTransferInfoMessage = ({
  transferInternalSettings,
  accountType,
  accountWithAssetOptions,
  assetSelectOptions,
  selectedAccountTypeTo,
  selectedAccountTypeFrom,
  watchedAssetFromCode,
  isAssetSelector,
  sectionType,
}: IInternalTransferInfoMessageParams) => {
  const infoMessageConfig = useMemo<InternalTransferInfoMessageConfig>(() => {
    if (!transferInternalSettings) {
      return INFO_MESSAGE_CONFIGS.default_option;
    }

    const hasNoOptions = isAssetSelector
      ? assetSelectOptions?.length === 0
      : accountWithAssetOptions.length === 0;

    if (sectionType === 'from') {
      if (hasNoOptions) {
        return INFO_MESSAGE_CONFIGS.no_convert_assets;
      }
    }

    if (sectionType === 'to') {
      if (
        hasNoOptions &&
        !watchedAssetFromCode &&
        (accountType === 'spot' || accountType === 'ewallet')
      ) {
        return INFO_MESSAGE_CONFIGS.no_convert_assets;
      }

      if (hasNoOptions && accountType === 'invest') {
        return INFO_MESSAGE_CONFIGS.no_convert_assets;
      }

      if (hasNoOptions && accountType === 'leverage') {
        return INFO_MESSAGE_CONFIGS.no_convert_assets;
      }

      if (hasNoOptions && accountType === 'earn') {
        return INFO_MESSAGE_CONFIGS.no_convert_assets;
      }

      if (
        hasNoOptions &&
        watchedAssetFromCode &&
        selectedAccountTypeFrom &&
        (selectedAccountTypeFrom === 'spot' ||
          selectedAccountTypeFrom === 'invest' ||
          selectedAccountTypeFrom === 'leverage')
      ) {
        const withdrawSettings =
          transferInternalSettings[
            selectedAccountTypeFrom as 'spot' | 'invest' | 'leverage' | 'earn'
          ];

        if (
          (withdrawSettings &&
            !withdrawSettings.isInternalWithdrawConvertAllowed) ||
          hasNoOptions
        ) {
          return INFO_MESSAGE_CONFIGS.no_convert_assets;
        }
      }

      if (
        selectedAccountTypeFrom === 'ewallet' &&
        selectedAccountTypeTo === 'ewallet'
      ) {
        return INFO_MESSAGE_CONFIGS.ewallet_ewallet;
      }

      if (
        selectedAccountTypeFrom === 'spot' &&
        selectedAccountTypeTo === 'spot'
      ) {
        return INFO_MESSAGE_CONFIGS.spot_spot;
      }
    }

    return INFO_MESSAGE_CONFIGS.default_option;
  }, [
    sectionType,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    transferInternalSettings,
    isAssetSelector,
    assetSelectOptions,
    accountWithAssetOptions,
    watchedAssetFromCode,
    accountType,
  ]);

  return {
    infoMessageConfig,
  };
};
